import {
	expressQuerySelector,
	expressEventListener,
	expressAddClass,
	expressQuerySelectorAll,
} from "../../common/html";
import { saveContentItemFieldAsync } from "../../services/inline-edit-service";
import { compositeDisposable } from "../../utils/disposable";
import { createFormInput } from "../atoms/form-input";
import { initForm } from "../molecules/form";
import { createPageLog, IPageLog, PageLogStyle } from "../molecules/page-log";
import { createPopup, IPopup } from "./popup-new";

let skipDiscardChangesCheck: boolean;
let mainPopup: IPopup;
let isFormChanged: boolean;
let pageLog: IPageLog;

async function openConfirmationPopupAsync(): Promise<void> {
	const confirmDiscardChangesDisposable = compositeDisposable([]);
	const confirmPopup = await createPopup({
		view: "GenericPopup",
		dialogId: "inlineedit-popup-confirmDiscardChanges",
		forceRefresh: false,
		closeOnClickOutsidePopup: false,
		data: {
			ExtraPopupCss: "p-default",
			TitleLabel: "Label.InlineEdit.ConfirmDiscardChanges.Title",
			BodyLabel: "Label.InlineEdit.ConfirmDiscardChanges.Description",
			OkButtonLabel: "Label.InlineEdit.ConfirmDiscardChanges.OkButtonText",
			CancelButtonLabel: "Label.InlineEdit.ConfirmDiscardChanges.CancelButtonText",
		},
		onClose: () => {
			confirmDiscardChangesDisposable.dispose();
		},
	});
	const okButton = expressQuerySelector(confirmPopup.popup, ".technical-general-popup-ok-button", true);
	const cancelButton = expressQuerySelector(confirmPopup.popup, ".technical-general-popup-cancel-button", true);

	// on confirm, close both popups
	confirmDiscardChangesDisposable.add(
		expressEventListener(okButton, "click", (_) => {
			// don't re-trigger the confirmDiscard-popup
			skipDiscardChangesCheck = true;
			confirmPopup.close();
			mainPopup.close();
		}),
	);

	// on cancel, close the confirm popup and keep the edit popup open
	confirmDiscardChangesDisposable.add(expressEventListener(cancelButton, "click", (_) => confirmPopup.close()));

	await confirmPopup.openAsync();
}

async function saveInlineEditChanges(data: { [inputName: string]: string }) {
	const result = await saveContentItemFieldAsync({
		pageId: mainPopup.popup.dataset.inlineeditPageid,
		contentItemId: mainPopup.popup.dataset.inlineeditContentitemid,
		contentKeyName: mainPopup.popup.dataset.inlineeditContentkeyname,
		fieldName: mainPopup.popup.dataset.inlineeditFieldname,
		fieldValue: data[""],
	});

	// Todo: handle errors
	if (!result.isSuccess) {
		if (!result.errorMessage) result.errorMessage = "Something went wrong. ";

		pageLog.updateMessageAndStyle(PageLogStyle.Error, result.errorMessage);
		return;
	}

	// show the success pagelog
	pageLog.updateMessageAndStyle(PageLogStyle.Success, result.successMessage);

	// hide the form and general data
	const contentDiv = expressQuerySelector<HTMLElement>(mainPopup.popup, ".technical-inlineedit-popup-content", true);
	const popupFooter = expressQuerySelector<HTMLElement>(mainPopup.popup, ".m-popup__footer", true);
	expressAddClass(contentDiv, "u-hide");
	expressAddClass(popupFooter, "u-hide");

	// don't trigger the confirmDiscard-popup
	skipDiscardChangesCheck = true;

	// no cache flush needed if nothing has changed
	if (isFormChanged) {
		// todo: flush cache for one key
	}

	setTimeout(() => {
		location.reload();
	}, 2500);
}

export async function initializeInlineEditPopupAsync(inlineEditEl: HTMLElement): Promise<IPopup> {
	skipDiscardChangesCheck = false;
	isFormChanged = false;

	const mainPopupDisposable = compositeDisposable([]);
	mainPopup = await createPopup({
		dialogId: "inlineedit-popup-details",
		baseUrl: "/apicore/inlineedit/",
		view: "getdetailspopup",
		forceRefresh: true,
		data: {
			pageid: inlineEditEl.dataset.inlineeditPageid,
			contentkeyname: inlineEditEl.dataset.inlineeditContentkeyname,
			fieldname: inlineEditEl.dataset.inlineeditFieldname,
		},
		onBeforeCloseAsync: async (_) => {
			// nothing changed, close the popup immediately
			if (!isFormChanged) {
				return true;
			}

			if (skipDiscardChangesCheck) return true;

			await openConfirmationPopupAsync();
			return false;
		},
		onClose: () => {
			mainPopupDisposable.dispose();
		},
	});

	if (!mainPopup.popup) return mainPopup;

	const pageLogEl = expressQuerySelector<HTMLElement>(mainPopup.popup, ".technical-inlineedit-popup-pagelog", true);
	pageLog = createPageLog(pageLogEl);

	const saveButton = expressQuerySelector<HTMLButtonElement>(
		mainPopup.popup,
		".technical-inlineedit-popup-save-button",
	);
	if (saveButton) {
		mainPopupDisposable.add(
			expressEventListener(saveButton, "click", () => {
				pageLog.hideLog();
			}),
		);
	}
	const cancelButton = expressQuerySelector<HTMLElement>(mainPopup.popup, ".technical-inlineedit-popup-cancel-button");
	if (cancelButton) {
		mainPopupDisposable.add(
			expressEventListener(cancelButton, "click", () => {
				mainPopup.close();
			}),
		);
	}
	const closeButton = expressQuerySelector<HTMLElement>(mainPopup.popup, ".technical-inlineedit-popup-close-button");
	if (closeButton) {
		mainPopupDisposable.add(
			expressEventListener(closeButton, "click", () => {
				mainPopup.close();
			}),
		);
	}
	const formEl = expressQuerySelector<HTMLFormElement>(mainPopup.popup, "form", false);
	if (formEl) {
		const fieldValueInput = createFormInput(mainPopup.popup);
		fieldValueInput.inputEl.addEventListener("input", () => {
			isFormChanged = true;
		});
		initForm(formEl, {
			alternativeButton: saveButton,
			onSubmitForm: saveInlineEditChanges,
		});
	}

	return mainPopup;
}
